import Decimal from 'decimal.js';
import { isNumber } from 'lodash';

import { getGrantorRelationshipsFriendlyString } from '@/modules/clientProfiles/clientProfileUtils';
import { testamentaryEntityKindToDisplayName } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { getDisplayNameFromEntityType } from '@/modules/entities/utils/getDisplayNameFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { EstateWaterfallSummaryTileSummaryPanelNode } from '@/modules/summaryPanels/EstateWaterfallSummmaryTileSummaryPanel/types';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatEnumCase } from '@/utils/formatting/strings';

import { SummaryModalState } from '../../contexts/waterfallSummary.context';
import { GetWaterfallSummary_EstateWaterfallVizNodeFragment } from '../../graphql/GetWaterfallSummary.generated';

/**
 * A waterfall viz node as displayed in the summary tiles. When a node is
 * split between the spouse and others tiles (partial spouse access), each
 * slice's `value` is scaled and `partialAccessFraction` carries the fraction
 * of the underlying node's value the slice represents.
 */
export type SummaryTileVizNode =
  GetWaterfallSummary_EstateWaterfallVizNodeFragment & {
    partialAccessFraction?: Decimal;
  };

export function formatCompactValue(value: number | Decimal) {
  return formatCurrency(isNumber(value) ? new Decimal(value) : value, {
    notation: 'compact',
  });
}

export function getNameFromNode(
  node: GetWaterfallSummary_EstateWaterfallVizNodeFragment['node']
) {
  if (node.__typename === 'ClientProfile') return node.displayName;
  if (node.__typename === 'TestamentaryEntity') return node.displayName;
  if (node.__typename === 'ClientOrganization') return node.name;
  if (node.__typename === 'Entity') return node.subtype.displayName;
  if (node.__typename === 'Proposal') return node.displayName;
  throw new Error(`Invalid node type: ${node.__typename}`);
}

export function getSubtitleFromNode(
  node: GetWaterfallSummary_EstateWaterfallVizNodeFragment['node']
) {
  if (node.__typename === 'ClientProfile') {
    const subtitle = getGrantorRelationshipsFriendlyString(node.relationships);
    // Skip grantor subtitle for self
    return subtitle.includes('Self') ? 'Client' : subtitle;
  }
  if (node.__typename === 'TestamentaryEntity') {
    return testamentaryEntityKindToDisplayName(node.testamentaryEntityKind);
  }
  if (node.__typename === 'ClientOrganization') {
    return formatEnumCase(node.clientOrganizationKind);
  }
  if (node.__typename === 'Entity') {
    return getDisplayNameFromEntityType(
      getEntityTypeFromEntityKind(node.entityKind)
    );
  }
  return '';
}

export function getSummaryTileNode(
  vizNode: SummaryTileVizNode
): EstateWaterfallSummaryTileSummaryPanelNode {
  return {
    id: vizNode.id,
    name: getNameFromNode(vizNode.node),
    subtitle: getSubtitleFromNode(vizNode.node) ?? '',
    value: vizNode.value,
    vizNode,
  };
}

export function isGrouped(title: string, data?: SummaryModalState | null) {
  return data?.type === 'summaryTileDetails' && data.title === title;
}

/** Must match the tiles' `transform: scale(...)` in LegacyEstateWaterfallSummarySlide. */
export const PRESENTATION_TILES_SCALE = 0.8;
/** Section label, stack gaps, row margins, and tile padding per section. */
const PRESENTATION_SECTION_OVERHEAD_PX = 75;
/** One MiniTable row (~21px line height + 8px spacing). */
const PRESENTATION_ROW_HEIGHT_PX = 29;
/** Floor on rows per tile; the shrink-to-fit never drops below this. */
export const PRESENTATION_MIN_ROWS = 3;
/** Used only until the tiles frame is measured (first render). */
const PRESENTATION_FALLBACK_FRAME_HEIGHT_PX = 478;

export interface PresentationMaxRowsParams {
  /** Measured on-slide height of the tiles frame; <= 0 means not yet measured. */
  availableHeightPx: number;
  sectionCount: number;
}

/**
 * Max line items per tile: fit rows into the frame height split across the
 * sections. Overflow collapses into a "(N more...)" row.
 */
export function getPresentationMaxRows({
  availableHeightPx,
  sectionCount,
}: PresentationMaxRowsParams): number {
  if (sectionCount <= 0) return PRESENTATION_MIN_ROWS;
  const frameHeightPx =
    availableHeightPx > 0
      ? availableHeightPx
      : PRESENTATION_FALLBACK_FRAME_HEIGHT_PX;
  const frameLayoutHeightPx = frameHeightPx / PRESENTATION_TILES_SCALE;
  const rowsBudgetPx =
    frameLayoutHeightPx / sectionCount - PRESENTATION_SECTION_OVERHEAD_PX;
  return Math.max(
    PRESENTATION_MIN_ROWS,
    Math.floor(rowsBudgetPx / PRESENTATION_ROW_HEIGHT_PX)
  );
}
